import { useRef, useEffect, useState } from 'react';

import { useRouter } from 'next/compat/router';
import Head from 'next/head';
import Link from 'next/link';
import Script from 'next/script';

import 'globalthis/auto';

import '@kittl/nprogress/nprogress.css';
import 'styles/globals.css';
import 'assets/styles/fonts.css';
import '@repo/ui/styles';

import '@repo/ui-kit/main.css';

import { usePromoCodeCookie } from '@editor/components/Subscription';
// Import analytics from the editor package to ensure it is initialized and configured
import analytics from '@editor/global/analytics';
import { isUserLoggedInOptimistic } from '@editor/stores/userStore/utils';
import { useGrowthBook } from '@growthbook/growthbook-react';
import NProgress from '@kittl/nprogress';
import { PrismicPreview } from '@prismicio/next';
import { PrismicProvider } from '@prismicio/react';
import { ResourceType } from '@repo/api-client';
import { areCookiesEnabled, isNativeApp } from '@repo/detection';
import AiCreditPurchaseModal from '@repo/editor/src/components/AiCreditsPurchaseModal/AiCreditPurchaseModal';
import Authentication from '@repo/editor/src/components/Authentication/Authentication';
import { ContextProvider } from '@repo/editor/src/components/context';
import LicensingModal from '@repo/editor/src/components/LicensingModal/LicensingModal';
import { PAYMENT_URL_PARAMS } from '@repo/editor/src/components/Payment';
import PaymentFailedModal from '@repo/editor/src/components/Subscription/PaymentFailedModal/PaymentFailedModal.tsx';
import UpgradeFlowModal from '@repo/editor/src/components/UpgradeFlowModal/UpgradeFlowModal';
import { IMPACT_CLICK_ID } from '@repo/editor/src/global/constants';
import { useOriginatingProfile } from '@repo/editor/src/hooks/useOriginatingProfile';
import {
  GrowthbookProvider,
  updateGrowthBookURL,
} from '@repo/editor/src/services/growthbook';
import { initMonitoring } from '@repo/editor/src/services/rum';
import {
  actionStoreSelector,
  useActionStore,
} from '@repo/editor/src/stores/actionStore/actionStore';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  notificationStoreSelector,
  useNotificationStore,
} from '@repo/editor/src/stores/notificationStore';
import {
  sharedConfigStoreSelector,
  useSharedConfigStore,
} from '@repo/editor/src/stores/sharedConfigStore';
import {
  teamsStoreSelector,
  useTeamsStore,
  ChooseTeamPlanFlowState,
  CreateTeamFlowState,
} from '@repo/editor/src/stores/teamsStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { setFetchClient } from '@repo/editor/src/utils/fetchRetry';
import { extractUtmData, persistUtmData } from '@repo/editor/src/utils/utm';
import env from '@repo/env';
import { EventCatcherScope } from '@repo/event-processor';
import { ResourceInfoWrapper } from '@repo/permissions';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
} from '@repo/subscription/client';
import { sendNavigationMessage } from '@repo/transport/webview';
import { OnlyDesktopAvailableModal } from '@repo/ui/deprecated/Modal';
import { UIRootWrapper } from '@repo/ui/index';
import { UIKitWrapper } from '@repo/ui-kit/components/utilities/Wrapper';
import { buildEditorUrl, buildWebsiteUrl } from '@repo/utils/url';
import Cookies from 'js-cookie';
import { IntercomProvider, useIntercom } from 'react-use-intercom';

import Banner from 'components/Banner/Banner';
import CanonicalLink from 'components/CanonicalLink/CanonicalLink';
import LoggedInFullScreenLoader from 'components/FullScreenLoader/LoggedInFullScreenLoader';
import { SidebarProvider } from 'components/Layout/Sidebar/SidebarProvider';
import OnboardingModalsWrapper from 'components/OnboardingModalsWrapper/OnboardingModalsWrapper';
import OneTapLogin from 'components/OneTapLogin/OneTapLogin';
import ProjectListModal from 'components/ProjectListModal/ProjectListModal';
import { ViewportContainer } from 'components/utilities/styles';
import { FACEBOOK_APP_ID } from 'global/config';
import { JAM_SCRIPT_URL } from 'global/constants';
import { INTERCOM_APP_ID } from 'global/intercomUtils';
import {
  CREATE_TEAM,
  NEW_PROJECT,
  CREATE_NEW_PROJECT_PARAMS,
} from 'global/urlUtils';
import { userVisitedEditor } from 'global/utils';
import { useAuthRedirect } from 'global/utils/authRedirectUtils';
import { useActiveWorkspaceId } from 'global/utils/workspace/useActiveWorkspaceId';
import { SubscriptionPackageProvider } from 'providers/SubscriptionProvider';
import { useAnnouncementStore } from 'stores/announcementStore/announcementStore';
import { bannerStoreSelector, useBannerStore } from 'stores/bannerStore';
import {
  mobileRoutingStoreSelector,
  useMobileRoutingStore,
} from 'stores/mobileRoutingStore';
import { useWorkspaceStore } from 'stores/workspaceStore';
import { GlobalStyles } from 'styles/globals';

import { initApiFetchClient } from '../global/utils/api';
import { linkResolver, repositoryName } from '../prismicio';
import QueryClientWrapper from '../queries/QueryClientWrapper';
import { useCasesStore, useCasesStoreSelector } from '../stores/useCasesStore';

initApiFetchClient();
NProgress.configure({ showSpinner: false });

const getOgImage = (path) => {
  try {
    return buildWebsiteUrl(path);
  } catch (e) {
    return 'https://cdn.shopify.com/s/files/1/0325/8312/3075/files/Kittl_Banner_OG.jpg?v=1648568266';
  }
};

const PrismicInternalLinkComponent = ({ href, children, ...props }) => (
  <Link href={href} {...props}>
    {children}
  </Link>
);

// permissions needed at high level but with access to growthbook provider
const ResourceInfoWrapperComponent = ({ children }) => {
  const activeWorkspaceId = useActiveWorkspaceId();
  const user = useUserStore(userStoreSelector.user);
  const personalWorkspaceId = user?.personalWorkspaceId;
  const resources = {
    [ResourceType.workspace]: activeWorkspaceId || personalWorkspaceId,
  };
  return <ResourceInfoWrapper {...resources}>{children}</ResourceInfoWrapper>;
};

const ExtraWrapper = ({ children }) => {
  const { boot, shutdown } = useIntercom();
  const user = useUserStore(userStoreSelector.user);

  const growthBook = useGrowthBook();

  const resetDesignStore = useDesignsStore(designsStoreSelector.reset);
  const router = useRouter();

  const setShowCreateTeamModal = useTeamsStore(
    teamsStoreSelector.setShowCreateTeamModal
  );
  const setCreateTeamFlowState = useTeamsStore(
    teamsStoreSelector.setCreateTeamFlowState
  );
  const setChooseTeamPlanFlowState = useTeamsStore(
    teamsStoreSelector.setChooseTeamPlanFlowState
  );

  const canUserCreateDesign = useDesignsStore(
    designsStoreSelector.canUserCreateDesign
  );
  const setAddToExistingDesignParams = useDesignsStore(
    designsStoreSelector.setAddToExistingDesignParams
  );

  useEffect(() => {
    const start = () => {
      const linkElement = document.activeElement;
      // Diable NProgress for links with [data-disable-nprogress]
      const shouldDisableProgress = linkElement?.closest(
        '[data-disable-nprogress="true"]'
      );

      if (!shouldDisableProgress) {
        NProgress.start();
      }
    };
    const done = () => {
      document.body.style.overflow = 'unset'; // reset remove scroll by Modal
      NProgress.done();

      // send the updated page to banner store show banner
      const currentPage = window.location.pathname;
      void useBannerStore.getState().init(user, growthBook, currentPage);
    };

    router.events.on('routeChangeStart', start);
    router.events.on('routeChangeComplete', done);
    router.events.on('routeChangeError', done);

    return () => {
      router.events.off('routeChangeStart', start);
      router.events.off('routeChangeComplete', done);
      router.events.off('routeChangeError', done);
    };
  }, [router, growthBook, user]);

  // Function to set up navigation event listeners in the mobile app WebView.
  // This is required since NextJS doesn't trigger navigation events
  // in the webview by default when navigating
  useEffect(() => {
    if (isNativeApp()) {
      // Handle Next.js route changes
      router.events.on('routeChangeStart', (url) => {
        sendNavigationMessage(url);
      });

      // Handle regular link clicks
      const handleLinkClick = (e) => {
        const link = e.target.closest('a');
        if (link && link.href) {
          sendNavigationMessage(link.href);
        }
      };
      document.addEventListener('click', handleLinkClick);

      return () => {
        document.removeEventListener('click', handleLinkClick);
      };
    }
  }, [router]);

  useEffect(() => {
    boot({
      email: user?.email,
      userHash: user?.intercomHMAC,
    });

    return () => {
      shutdown();
    };
  }, [user?.email, user?.intercomHMAC, boot, shutdown]);

  useEffect(() => {
    void useAnnouncementStore.getState().init(user, growthBook);
    const currentPage = window.location.pathname;
    void useBannerStore.getState().init(user, growthBook, currentPage);

    if (!user) {
      // reset designStore on logout
      resetDesignStore();
    }
  }, [user, growthBook, resetDesignStore]);

  useEffect(() => {
    // Update the growthbook URL
    updateGrowthBookURL();

    if (!user) {
      return;
    }
    const routerQuery = { ...router.query };
    let needUpdate = false;
    // checks limit for links to the editor set externally
    if (routerQuery[NEW_PROJECT]) {
      delete routerQuery[NEW_PROJECT];

      const queryParams = new URLSearchParams(routerQuery);
      canUserCreateDesign({
        canShowProjectListModal: true,
        redirectUrl: `?${queryParams.toString()}`,
        cb: () => window.open(buildEditorUrl(queryParams.toString()), '_blank'),
      });

      Object.keys(routerQuery).forEach((key) => {
        if (CREATE_NEW_PROJECT_PARAMS.includes(key)) {
          delete routerQuery[key];
        }
      });
      needUpdate = true;
    }

    if (routerQuery[CREATE_TEAM]) {
      setShowCreateTeamModal(true);
      setCreateTeamFlowState(CreateTeamFlowState.CreateTeam);
      setChooseTeamPlanFlowState(ChooseTeamPlanFlowState.ChooseTeamPlan);
      delete routerQuery[CREATE_TEAM];
      needUpdate = true;
    } else if (PAYMENT_URL_PARAMS.some((param) => routerQuery[param])) {
      if (user.isTeam) {
        setShowCreateTeamModal(true);
        setCreateTeamFlowState(CreateTeamFlowState.ChoosePlan);
        setChooseTeamPlanFlowState(ChooseTeamPlanFlowState.Success);
      }

      PAYMENT_URL_PARAMS.forEach((param) => {
        delete routerQuery[param];
      });
      needUpdate = true;
    }

    if (needUpdate) {
      const { pathname } = router;
      router.replace({ pathname, query: routerQuery }, undefined, {
        shallow: true,
      });
    }
  }, [
    router,
    router.query,
    user,
    setShowCreateTeamModal,
    setCreateTeamFlowState,
    setChooseTeamPlanFlowState,
    canUserCreateDesign,
    setAddToExistingDesignParams,
  ]);

  return children;
};

const MyApp = ({ Component, pageProps, router }) => {
  const user = useUserStore(userStoreSelector.loadingUser);

  const rootContainer = useRef();
  const initialPageViewTracked = useRef(false);
  const hasBanner = useBannerStore(bannerStoreSelector.isVisible);

  useEffect(() => {
    // Coralogix doesn't have support for modules or transpile yet, also they recommend to run it inside a component not at instrumentation level
    initMonitoring().then(() => setFetchClient()); // Workaround to re-create fetch client with Coralogix header injection
  }, []);

  const isMobileModalOpen = useMobileRoutingStore(
    mobileRoutingStoreSelector.isOpen
  );
  const closeMobileModal = useMobileRoutingStore(
    mobileRoutingStoreSelector.closeModal
  );

  const initializeUser = useUserStore((state) => state.initialize);
  const initializeWorkspaceStore = useWorkspaceStore(
    (state) => state.initialize
  );
  const initializeNotifications = useNotificationStore(
    notificationStoreSelector.init
  );
  const resetNotifications = useNotificationStore(
    notificationStoreSelector.reset
  );
  const initActionStore = useActionStore(actionStoreSelector.init);
  const resetActionStore = useActionStore(actionStoreSelector.reset);
  const registerBeforeLoginAction = useUserStore(
    (state) => state.registerBeforeLoginAction
  );

  const initializeUseCases = useCasesStore(useCasesStoreSelector.initialize);

  useAuthRedirect();

  const { init: initSharedConfigStore } = useSharedConfigStore(
    sharedConfigStoreSelector.actions
  );

  useEffect(() => {
    void initSharedConfigStore();
  }, [initSharedConfigStore]);

  useEffect(() => {
    if (areCookiesEnabled()) {
      registerBeforeLoginAction(() => {
        resetNotifications();
        initializeNotifications();
      });

      registerBeforeLoginAction(() => {
        resetActionStore();
        void initActionStore();
      });

      registerBeforeLoginAction((user) => {
        if (env.nodeEnv !== 'development' && userVisitedEditor()) {
          import('refiner-js')
            .then((refiner) => {
              if (process.env.NEXT_PUBLIC_REFINER_PROJECT_ID) {
                refiner.default(
                  'setProject',
                  process.env.NEXT_PUBLIC_REFINER_PROJECT_ID
                );
                refiner.default('identifyUser', {
                  id: user.email,
                  email: user.email,
                  name: user.name,
                  plan: user.plan || 'FREE',
                });
              }
            })
            .catch((err) =>
              //eslint-disable-next-line no-console
              console.log(`
            Could not load refiner!\n
            ${err}
          `)
            );
        }
      });

      initializeUser();
      initializeUseCases();
      initializeWorkspaceStore();
    }
  }, [
    initializeUser,
    registerBeforeLoginAction,
    resetNotifications,
    initializeNotifications,
    initActionStore,
    resetActionStore,
    initializeUseCases,
    initializeWorkspaceStore,
  ]);

  useEffect(() => {
    // Track page changes
    const handleRouteChange = () => {
      // Don't track if initial page view wasn't tracked yet
      if (initialPageViewTracked.current) {
        analytics.page({
          event: 'virtualPageview',
          userId: user?.id,
          plan: user?.plan || (user ? 'FREE' : undefined),
        });
      }
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, user]);

  useEffect(() => {
    // Track initial page load
    if (user !== false && !initialPageViewTracked.current) {
      initialPageViewTracked.current = true;
      analytics.page({
        userId: user?.id,
        plan: user?.plan || (user ? 'FREE' : undefined),
      });
    }
  }, [user]);

  const [cioSource, setCioSource] = useState('');

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !env.customerIoSiteId ||
      env.nodeEnv !== 'production'
    ) {
      return;
    }

    // only add customer io track script on client side
    setCioSource('https://assets.customer.io/assets/track-eu.js');
  }, []);

  const [gtmSource, setGtmSource] = useState('');

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !env.gtmId ||
      env.nodeEnv !== 'production'
    ) {
      return;
    }

    // only add gtm script on client side
    setGtmSource(`https://www.googletagmanager.com/gtm.js?id=${env.gtmId}`);
  }, []);

  const [jamSource, setJamSource] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined' || env.nodeEnv !== 'production') {
      return;
    }

    // only add Jam script on client side
    setJamSource(JAM_SCRIPT_URL);
  }, []);

  const IMPACT_CLICK_ID_PARAM = 'irclickid';

  usePromoCodeCookie();

  useEffect(() => {
    // Only first value is stored in cookie
    if (
      typeof window === 'undefined' ||
      typeof Cookies.get(IMPACT_CLICK_ID) === 'string'
    ) {
      return;
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const impactClickId = urlParams.get(IMPACT_CLICK_ID_PARAM);
    if (impactClickId) {
      Cookies.set(IMPACT_CLICK_ID, impactClickId);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    const utmData = extractUtmData(urlParams);

    if (!utmData) {
      return;
    }

    persistUtmData(utmData);
  }, []);

  // `RELOADED_FLAG_PARAM` acts as guard to prevent an endless loop in case
  // for some reason the middleware isn't redirecting.
  const RELOADED_FLAG_PARAM = 'loginFlag';

  const triggerMiddlewareOnHome = () => {
    // HACK: We don't know why the user is not always redirected by the
    // middleware. For now, we use this reload to trigger it, to catch any
    // redirects/rewrites. It also seems adding `RELOADED_FLAG_PARAM`
    // influences the middleware behaviour.

    const { query, pathname } = router;
    const params = new URLSearchParams(query);

    if (pathname === '/' && !params.has(RELOADED_FLAG_PARAM)) {
      params.append(RELOADED_FLAG_PARAM, 't');
      router.replace({ pathname, query: params.toString() });
    }
  };

  registerBeforeLoginAction(triggerMiddlewareOnHome);
  useOriginatingProfile();

  // The PaymentFailedModal only shows on these paths and also follows conditional logic inside (feature flags, etc.), but we want to avoid adding path matching to the modal itself (to keep the one-direction rule)
  const showPaymentFailedModal = [/^\/$/i, /^\/user\/dashboard/i].some((p) =>
    p.test(router.asPath?.split('?')[0])
  );

  // We have this true by default to avoid hydration errors
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(true);
  const isUserMaybeLoggedIn = isUserLoggedInOptimistic();

  // Due to fetching the subscription after initializing workspace, we'll have a few minutes where
  // the user is logged in but assumed to be on a free plan.
  const subscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  useEffect(() => {
    setShowFullScreenLoader(user && subscription ? false : isUserMaybeLoggedIn);
  }, [isUserMaybeLoggedIn, user, subscription]);

  return (
    <>
      <Head>
        <title>Kittl</title>
        <link href="/favicon.ico" rel="icon" />
        <link
          href="/apple-touch-icon.png"
          rel="apple-touch-icon"
          sizes="192x192"
        />
        <link
          href="/apple-touch-icon-120x120.png"
          rel="apple-touch-icon"
          sizes="120x120"
        />
        <link
          href="/apple-touch-icon-120x120-precomposed.png"
          rel="apple-touch-icon-precomposed"
          sizes="120x120"
        />
        <link
          href="/apple-touch-icon-precomposed.png"
          rel="apple-touch-icon-precomposed"
          sizes="192x192"
        />
        <meta
          key="description"
          content="Kittl is the most intuitive and easy-to-use design platform which helps you to create stunning designs that impress everyone. Easily learn new design techniques and improve yourself to become a better creator."
          name="description"
        />
        <meta content="Kittl" name="apple-mobile-web-app-title" />
        <meta
          content="width=device-width, initial-scale=1, maximum-scale=1"
          name="viewport"
        />
        <meta content="nohover" name="pinterest" />
        {/* Open Graph settings. To overwrite this tags on any other page make sure to use the same 'key' attribute.  */}
        <meta
          key="og:title"
          content="Kittl - Unleash Your Creative Power"
          property="og:title"
        />
        <meta key="og:url" content={buildWebsiteUrl()} property="og:url" />
        <meta key="og:type" content="website" property="og:type" />
        <meta
          key="og:image"
          content={getOgImage('/images/og-banner.jpg')}
          property="og:image"
        />
        <meta key="og:image-width" content="1200" property="og:image:width" />
        <meta key="og:image-height" content="630" property="og:image:height" />
        <meta
          key="og:description"
          content="Kittl is the most intuitive and easy-to-use design platform which helps you to create stunning designs that impress everyone. Easily learn new design techniques and improve yourself to become a better creator."
          property="og:description"
        />
        <meta key="fb:app_id" content={FACEBOOK_APP_ID} property="fb:app_id" />
        <meta key="og:locale" content="en_US" property="og:locale" />
        {process.env.NEXT_PUBLIC_NO_INDEX === 'true' ? (
          <meta key="robots" content="noindex,nofollow" name="robots" />
        ) : null}
      </Head>
      <CanonicalLink />
      <Script async defer id="jam-script" src={jamSource} />
      {/* Fix for the analytics plugin DOM changes being replaced by Next.js */}
      {env.customerIoSiteId && (
        <Script
          defer
          data-site-id={env.customerIoSiteId}
          data-use-in-app="true"
          id="cio-tracker"
          src={cioSource}
        />
      )}
      {env.gtmId && (
        <Script
          defer
          id="gtm-script"
          src={gtmSource}
          strategy="afterInteractive"
        />
      )}

      <EventCatcherScope isLoggedIn={!!user}>
        <GrowthbookProvider showWhileLoading>
          <QueryClientWrapper>
            <UIKitWrapper>
              <UIRootWrapper>
                <GlobalStyles />
                <ContextProvider
                  value={{
                    getRootContainer: () => {
                      return rootContainer.current;
                    },
                  }}
                >
                  <IntercomProvider appId={INTERCOM_APP_ID}>
                    <SubscriptionPackageProvider>
                      <ResourceInfoWrapperComponent>
                        <ExtraWrapper>
                          <LicensingModal
                            dismissable={
                              router.pathname !== '/licensing/update-may-2023'
                            }
                          />
                          <Authentication />
                          <OnboardingModalsWrapper />
                          {showPaymentFailedModal && <PaymentFailedModal />}
                          <UpgradeFlowModal />
                          <AiCreditPurchaseModal />
                          <OnlyDesktopAvailableModal
                            isOpen={isMobileModalOpen}
                            onClose={closeMobileModal}
                          />
                          <ProjectListModal />
                          <PrismicProvider
                            internalLinkComponent={PrismicInternalLinkComponent}
                            linkResolver={linkResolver}
                          >
                            <PrismicPreview repositoryName={repositoryName}>
                              <OneTapLogin />
                              {hasBanner && <Banner />}
                              <SidebarProvider>
                                {showFullScreenLoader && (
                                  <LoggedInFullScreenLoader
                                    isDark={false}
                                    opacity={1}
                                  />
                                )}
                                <Component {...pageProps} />
                              </SidebarProvider>
                            </PrismicPreview>
                          </PrismicProvider>
                        </ExtraWrapper>
                      </ResourceInfoWrapperComponent>
                      <ViewportContainer ref={rootContainer} />
                    </SubscriptionPackageProvider>
                  </IntercomProvider>
                </ContextProvider>
              </UIRootWrapper>
            </UIKitWrapper>
          </QueryClientWrapper>
        </GrowthbookProvider>
      </EventCatcherScope>
    </>
  );
};

export default MyApp;
